import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

const bounceTransition = {
  duration: 0.6,
  repeat: Infinity,
  repeatType: 'mirror',
  ease: 'easeInOut',
};



const loadingSteps = [
  { text: 'Fetching data from Reltio...', delay: 0 },
  { text: 'Invoking AI agent for additional insights...', delay: 4000 },
  { text: 'Scraping missing information from web sources...', delay: 15000 },
  { text: 'This is taking longer than expected. Please wait...', delay: 60000 },
];

function Loader() {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const currentStep = loadingSteps[messageIndex];
    if (!currentStep) return;

    const timeout = setTimeout(() => {
      if (messageIndex < loadingSteps.length - 1) {
        setMessageIndex((prev) => prev + 1);
      }
    }, currentStep.delay === 0 ? 4000 : loadingSteps[messageIndex].delay - loadingSteps[messageIndex - 1]?.delay);

    return () => clearTimeout(timeout);
  }, [messageIndex]);

  const loadingText = loadingSteps[messageIndex]?.text;

  return (
    <Box
      sx={{
        height: '50vh',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'transparent',
      }}
    >
      {/* Animated Gradient Dots */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            style={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              background: 'linear-gradient(270deg, #08AA66, #1C2758, #08AA66)',
              backgroundSize: '300% 300%',
              animation: 'dotGradientMove 4s ease infinite',
              display: 'inline-block',
              boxShadow: '0 0 8px rgba(8,170,102,0.4), 0 0 16px rgba(28,39,88,0.2)',
            }}
            animate={{ y: ['0%', '-60%', '0%'] }}
            transition={{ ...bounceTransition, delay: i * 0.2 }}
          />
        ))}
      </Box>

      {/* Animated Gradient Text */}
      <AnimatePresence mode="wait">
        <motion.div
          key={loadingText}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              textAlign: 'center',
              minHeight: '1.5rem',
              px: 2,
              background: 'linear-gradient(270deg, #08AA66, #1C2758, #08AA66)',
              backgroundSize: '400% 100%',
              animation: 'textGradientMove 6s ease infinite',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            {loadingText}
          </Typography>
        </motion.div>
      </AnimatePresence>

      {/* Keyframes for gradient animation */}
      <style>{`
        @keyframes textGradientMove {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        @keyframes dotGradientMove {
          0% { background-position: 0% 0%; }
          50% { background-position: 100% 100%; }
          100% { background-position: 0% 0%; }
        }
      `}</style>
    </Box>
  );
}

export default Loader;
